import { useCallback, useEffect } from 'react';
import { atom, useAtom } from 'jotai';
import { useLatest } from 'ahooks';
import { useUserLoggedIn } from '~/modules/user';
import type { Conversation, DirectConversation } from '../types';

type UpdatableConversationProperties = Pick<
    DirectConversation,
    | 'UnreadMessageCount'
    | 'Unread'
    | 'IsArchived'
    | 'IsMuted'
    | 'Name'
    | 'BuildHub'
    | 'Thumbnail'
    | 'Participants'
> & {
    Messages?: DirectConversation['Messages']['nodes'];
};

/**
 * The maximum number of conversations to cache. Make sure this is always larger
 * than the maximum number of open chat boxes.
 */
const MAX_CACHED_CONVERSATIONS = 20;

/**
 * The list of conversations that have been cached, ordered from newest to oldest.
 */
const conversationCacheAtom = atom<Conversation[]>([]);
/**
 * The latest cached conversation.
 */
const lastCachedConversationAtom = atom<Conversation | null>(null);

/**
 * Hook to manage the conversation cache.
 */
export function useConversationCache() {
    const { isLoggedIn } = useUserLoggedIn();
    const [conversationCache, setConversationCache] = useAtom(
        conversationCacheAtom
    );
    const [lastCachedConversation, setLastCachedConversation] = useAtom(
        lastCachedConversationAtom
    );

    const getCachedConversation = useCallback(
        (conversationID: number) => {
            return conversationCache.find(
                (c) => c.ID === String(conversationID)
            );
        },
        [conversationCache]
    );
    // Use latest ref to be passed to dependency array to prevent unnecessary re-renders.
    const getCachedConversationRef = useLatest(getCachedConversation);

    const getCachedConversationByParticipants = useCallback(
        (userID: number, memberIDs: number[]) => {
            if (memberIDs.length === 0) {
                return undefined;
            }
            const conversations = conversationCache.filter(
                (c) =>
                    c.Participants.length === memberIDs.length + 1 &&
                    c.Participants.some(
                        ({ Identity }) =>
                            Identity.__typename === 'Member' &&
                            Identity.ID === userID
                    ) &&
                    memberIDs.every((memberID) =>
                        c.Participants.some(
                            ({ Identity }) =>
                                Identity.__typename === 'Member' &&
                                Identity.ID === memberID
                        )
                    )
            );
            if (conversations.length <= 1) {
                return conversations[0];
            }
            // If more than one exists, first return the non-group chat one.
            const nonGroupChat = conversations.find(
                (c) => c.__typename === 'DirectConversation' && !c.IsGroupChat
            );
            if (nonGroupChat) {
                return nonGroupChat;
            }
            // If more than one group chat exists, return the one with the latest message.
            conversations.sort((a, b) => {
                const aLatestMessage = new Date(
                    a.Messages.nodes[0]?.Created ?? 0
                ).getTime();
                const bLatestMessage = new Date(
                    b.Messages.nodes[0]?.Created ?? 0
                ).getTime();
                return bLatestMessage - aLatestMessage;
            });
            return conversations[0];
        },
        [conversationCache]
    );

    const cacheConversation = useCallback(
        (conversation: Conversation) => {
            setConversationCache((prev) => {
                return [
                    conversation,
                    ...prev.filter((c) => c.ID !== String(conversation.ID)),
                ].slice(0, MAX_CACHED_CONVERSATIONS);
            });
            setLastCachedConversation(conversation);
        },
        [setConversationCache, setLastCachedConversation]
    );

    const updateCachedConversation = useCallback(
        (
            conversationID: number,
            updates: Partial<UpdatableConversationProperties>
        ) => {
            const cachedConversation =
                getCachedConversationRef.current(conversationID);
            if (!cachedConversation) {
                return;
            }
            // Only update fields that are not `undefined`.
            const updatedConversation: Conversation = {
                ...cachedConversation,
                ...(updates.UnreadMessageCount !== undefined
                    ? { UnreadMessageCount: updates.UnreadMessageCount }
                    : {}),
                ...(updates.Unread !== undefined
                    ? { Unread: updates.Unread }
                    : {}),
                ...(updates.IsArchived !== undefined
                    ? { IsArchived: updates.IsArchived }
                    : {}),
                ...(updates.IsMuted !== undefined
                    ? { IsMuted: updates.IsMuted }
                    : {}),
                ...(updates.Name !== undefined ? { Name: updates.Name } : {}),
                ...(updates.BuildHub !== undefined
                    ? { BuildHub: updates.BuildHub }
                    : {}),
                ...(updates.Thumbnail !== undefined
                    ? { Thumbnail: updates.Thumbnail }
                    : {}),
            };
            if (updatedConversation.__typename === 'DirectConversation') {
                // Append new messages to the cached messages.
                const cachedMessages = updates.Messages?.length
                    ? updatedConversation.Messages.nodes
                    : null;
                if (cachedMessages && updates.Messages) {
                    for (const message of updates.Messages) {
                        if (!cachedMessages.some((m) => m.ID === message.ID)) {
                            cachedMessages.unshift(message);
                        }
                    }
                }
                cacheConversation({
                    ...updatedConversation,
                    ...(updates.Participants !== undefined
                        ? { Participants: updates.Participants }
                        : {}),
                    ...(cachedMessages?.length
                        ? {
                              Messages: {
                                  ...updatedConversation.Messages,
                                  nodes: cachedMessages,
                              },
                          }
                        : {}),
                });
            } else {
                cacheConversation(updatedConversation);
            }
        },
        [getCachedConversationRef, cacheConversation]
    );

    const replaceCachedConversationMessages = useCallback(
        (
            conversationID: number,
            messages: DirectConversation['Messages']['nodes']
        ) => {
            const cachedConversation =
                getCachedConversationRef.current(conversationID);
            if (!cachedConversation) {
                return;
            }
            if (cachedConversation.__typename === 'DirectConversation') {
                cacheConversation({
                    ...cachedConversation,
                    Messages: {
                        ...cachedConversation.Messages,
                        nodes: messages,
                    },
                });
            } else {
                console.warn(
                    'updateCachedConversationMessages: Not a direct conversation'
                );
            }
        },
        [getCachedConversationRef, cacheConversation]
    );

    const invalidateCachedConversation = useCallback(
        (conversationID: number) => {
            setConversationCache((prev) => {
                // Conditionally set new value to avoid unnecessary re-renders.
                if (prev.some((c) => c.ID === String(conversationID))) {
                    return prev.filter((c) => c.ID !== String(conversationID));
                }
                return prev;
            });
            setLastCachedConversation((prev) => {
                if (prev?.ID === String(conversationID)) {
                    return null;
                }
                return prev;
            });
        },
        [setConversationCache, setLastCachedConversation]
    );

    // Clear the conversation cache when the user logs out.
    useEffect(() => {
        if (!isLoggedIn) {
            setConversationCache([]);
            setLastCachedConversation(null);
        }
    }, [isLoggedIn, setConversationCache, setLastCachedConversation]);

    return {
        /**
         * The latest cached conversation.
         */
        lastCachedConversation,
        /**
         * Get the cached conversation by the conversation ID.
         */
        getCachedConversation,
        /**
         * Get the cached conversation by the participants.
         */
        getCachedConversationByParticipants,
        /**
         * Cache a conversation. If the number of cached conversations exceeds the
         * maximum, the oldest conversation will be removed.
         */
        cacheConversation,
        /**
         * Update a subset of fields of a cached (direct) conversation, if the
         * conversation is already cached. If conversation messages are passed,
         * they will be appended to the current cached conversation messages
         * (not replaced).
         */
        updateCachedConversation,
        /**
         * Replace the messages of a cached (direct) conversation, if the
         * conversation is already cached.
         */
        replaceCachedConversationMessages,
        /**
         * Invalidate a cached conversation.
         */
        invalidateCachedConversation,
    };
}
